// 🔧 content_locales.js
// ✅ Purpose: Defines canonical locales used by the Content Engine
// ✅ Features:
// - Establishes en-US as the canonical source locale
// - Prepares for future Localization Engine support
// - Separates language from Content Profiles and Markets
// - Centralizes locale values for resolver, cache, permissions, and bundle generation
// - Supports future locale fallback architecture
// - Phase 1 uses en-US only

/**
 * ===========================================================================
 * LOCALE PHILOSOPHY
 * ===========================================================================
 *
 * A Locale answers:
 *
 *      "What language is this content written in?"
 *
 * Examples:
 *
 *      en-US
 *      es-MX
 *      es-ES
 *      fr-CA
 *
 * Locales DO NOT determine:
 *
 *      Market
 *      Audience
 *      Branding
 *      Organization
 *
 * Those responsibilities belong to:
 *
 *      Profiles
 *      Markets
 *      Scopes
 *
 * This separation keeps the architecture flexible as LGV grows.
 *
 * ===========================================================================
 */

export const CONTENT_LOCALES = {
  EN_US: "en-US",

  ES_MX: "es-MX",
  ES_ES: "es-ES",

  FR_CA: "fr-CA",

  PT_BR: "pt-BR",

  DE_DE: "de-DE",

  IT_IT: "it-IT",

  JA_JP: "ja-JP",

  KO_KR: "ko-KR",
};

/**
 * Human-readable locale names.
 */

export const CONTENT_LOCALE_LABELS = {
  [CONTENT_LOCALES.EN_US]:
    "English (United States)",

  [CONTENT_LOCALES.ES_MX]:
    "Spanish (Mexico)",

  [CONTENT_LOCALES.ES_ES]:
    "Spanish (Spain)",

  [CONTENT_LOCALES.FR_CA]:
    "French (Canada)",

  [CONTENT_LOCALES.PT_BR]:
    "Portuguese (Brazil)",

  [CONTENT_LOCALES.DE_DE]:
    "German (Germany)",

  [CONTENT_LOCALES.IT_IT]:
    "Italian (Italy)",

  [CONTENT_LOCALES.JA_JP]:
    "Japanese (Japan)",

  [CONTENT_LOCALES.KO_KR]:
    "Korean (South Korea)",
};

/**
 * Canonical source locale.
 *
 * All Content Engine content begins life in this locale.
 *
 * Future localized content will reference this version
 * as the source of truth.
 */

export const DEFAULT_CONTENT_LOCALE =
  CONTENT_LOCALES.EN_US;

/**
 * ===========================================================================
 * LOCALE FALLBACK
 * ===========================================================================
 *
 * Phase 1:
 *
 * Requested Locale
 *          ↓
 * en-US
 *          ↓
 * Code Fallback
 *
 * Future:
 *
 * Requested Locale
 *          ↓
 * Locale Profile
 *          ↓
 * Locale Default
 *          ↓
 * en-US Profile
 *          ↓
 * en-US Default
 *          ↓
 * Code Fallback
 *
 * ===========================================================================
 */

export const LOCALE_FALLBACK_ORDER = [
  "requested_locale_profile",
  "requested_locale",
  "default_locale_profile",
  "default_locale",
  "code_default",
];

/**
 * ===========================================================================
 * FUTURE TRANSLATION STATUS
 * ===========================================================================
 *
 * Included now so every localized document can
 * use a consistent lifecycle.
 *
 * Phase 1 does not implement localization UI.
 *
 * ===========================================================================
 */

export const LOCALIZATION_STATUSES = {
  MISSING: "missing",

  AI_DRAFT: "ai_draft",

  HUMAN_REVIEW: "human_review",

  APPROVED: "approved",

  PUBLISHED: "published",

  STALE: "stale",
};

export const LOCALIZATION_STATUS_LABELS = {
  [LOCALIZATION_STATUSES.MISSING]:
    "Missing",

  [LOCALIZATION_STATUSES.AI_DRAFT]:
    "AI Draft",

  [LOCALIZATION_STATUSES.HUMAN_REVIEW]:
    "Human Review",

  [LOCALIZATION_STATUSES.APPROVED]:
    "Approved",

  [LOCALIZATION_STATUSES.PUBLISHED]:
    "Published",

  [LOCALIZATION_STATUSES.STALE]:
    "Stale",
};